import BidEstimateIconRed from "@/assets/images/Tab/bid-estimate-icon-red.png";
import Card from "@/components/Common/Card";
import { ItemSheetStatus } from "@/consts/Estimate";
import { ProjectType } from "@/consts/Project";
import { useRestartItemsheetOriginal } from "@/libs/react-query/hooks/useItemsheetsQuery";
import { showToast } from "@/utils/toastUtils";
import { useEffect, useState } from "react";

// 서버 규칙: REQUESTED/PROCESSING 상태는 updated_at이 10분 이상 지나야 재시작 가능
const RESTARTABLE_AFTER_MS = 10 * 60 * 1000;

type PendingOriginal = {
  project_id: string;
  project_type: ProjectType;
  status: ItemSheetStatus | null;
  progress_step?: string | null;
  progress_done?: number | null;
  progress_total?: number | null;
  updated_at?: number; // unix seconds
};

type Props = {
  groupId: string;
  original: PendingOriginal;
};

export default function PendingEstimate({ groupId, original }: Props) {
  const { status, progress_step, progress_done, progress_total, updated_at } =
    original;
  const isFailed = status === ItemSheetStatus.FAILED;

  // 재시작 버튼 노출 조건(10분 경과)을 다시 계산하기 위한 시계
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  const isStale =
    updated_at != null && now - updated_at * 1000 > RESTARTABLE_AFTER_MS;
  const canRestart =
    isFailed ||
    ((status === ItemSheetStatus.REQUESTED ||
      status === ItemSheetStatus.PROCESSING) &&
      isStale);

  const { mutate: restart, isPending } = useRestartItemsheetOriginal(groupId);

  const handleRestart = () => {
    if (!window.confirm("산출내역서 생성을 다시 시작할까요?")) return;

    restart(
      {
        project_id: original.project_id,
        project_type: original.project_type,
      },
      {
        onSuccess: () => {
          showToast({ message: "산출내역서 생성을 다시 시작했습니다." });
        },
        onError: (error: any) => {
          showToast({
            message: "재시작에 실패했습니다.",
            type: "error",
            description: error?.response?.data?.display_message || "",
          });
        },
      }
    );
  };

  const hasProgress =
    progress_done != null && progress_total != null && progress_total > 0;
  const percent = hasProgress
    ? Math.min(100, Math.round((progress_done / progress_total) * 100))
    : null;

  return (
    <Card icon={BidEstimateIconRed} title="산출내역서">
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div className="flex flex-col w-full h-full items-center justify-center gap-5 py-14">
          <div className="px-6 py-4 bg-gray-D rounded-full shadow-md">
            <div className="flex gap-1.5 text-2xl text-gray-B font-bold">
              산출내역서
              <span className="text-dark-red">
                {isFailed ? "생성 실패" : "생성 중"}
              </span>
            </div>
          </div>

          {!isFailed && (
            <div className="flex flex-col items-center gap-3">
              {progress_step && (
                <div className="flex items-center gap-2 text-base text-gray-A font-semibold animate-pulse">
                  <div className="w-4 h-4 border-2 border-gray-D border-t-dark-red rounded-full animate-spin" />
                  {progress_step}
                </div>
              )}
              {hasProgress && (
                <div className="flex flex-col items-center gap-1.5">
                  <div className="w-64 h-2 bg-gray-D rounded-full overflow-hidden">
                    <div
                      className="h-full bg-dark-red rounded-full transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-B font-medium">
                    {progress_done}/{progress_total}
                  </span>
                </div>
              )}
            </div>
          )}

          <p className="text-lg text-gray-B font-semibold">
            {isFailed
              ? "산출내역서 생성에 실패했습니다"
              : "산출내역서 생성이 완료되면 메일로 알려드려요"}
          </p>

          {canRestart && (
            <button
              type="button"
              onClick={handleRestart}
              disabled={isPending}
              className="px-5 py-2 rounded-lg bg-dark-red text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {isPending ? "재시작 중..." : "재시작"}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
